import {
  Checkbox,
  ContentSwitcher,
  Field,
  IconButton,
  Menu,
  Modal,
  NumberInput,
  RotateCwIcon,
  Scrollbar,
  SingleSelectDropdown,
  Spinner,
  Switch,
  TextInput,
  toast,
} from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useRef, useState } from "react";
import { appTextModes } from "@/shared/ui/text";
import { CopyButton } from "@/shared/ui/CopyButton";
import { StatusChip, type StatusTone } from "@/shared/ui/status";
import { formatDateTime } from "@/shared/utils/datetime/format";
import type { EpisodeInspect } from "../../../domain/episodeInspect";
import {
  HAND_ROLE_NAMES,
  isPocFinished,
  type HandRoleName,
  type Mapping,
  type MappingIssue,
  type PocJob,
  type PocJobState,
  repeatedColumnPaths,
} from "../../../domain/mapping";
import type { StoredMapping } from "../../../domain/storedMapping";
import {
  addCamera,
  cameraRows,
  HAND_LAYOUTS,
  handLayout,
  removeCameras,
  setActionEnabled,
  setCameraSlot,
  sameMapping,
  setHandLayout,
  updateRole,
  type HandLayout,
} from "../../../feature/mappingEdit";
import {
  canSendDraft,
  canRequestPoc,
  currentPocValidation,
  pocNeedsReview,
  hasUnappliedJson,
  hasUnsavedEdits,
  isEditable,
  type MappingEditMode,
  type MappingEditor,
  type MappingEditorState,
  type MappingField,
  type SaveIssues,
  type SaveOutcome,
} from "../../../feature/mappingEditor";
import { formatMappingJson } from "../../../feature/mappingJson";
import {
  EMPTY_ISSUE_INDEX,
  ROLE_FIELDS,
  fieldKey,
  indexIssues,
  issueTarget,
  roleKey,
  type IssueIndex,
  type IssueTarget,
} from "../../../feature/issuePath";
import {
  describeMappingPath,
  HAND_ROLE_LABEL,
  HAND_SIDE_LABEL,
  ROLE_FIELD_LABEL,
} from "../../../feature/mappingPath";
import { mappingReviewItems, validationSummary } from "../../../feature/mappingReview";
import { IssueList } from "./IssueList";
import { MappingJsonPane } from "./MappingJsonPane";
import { MappingRefreshStatus } from "./MappingRefreshStatus";
import { MappingReviewList } from "./MappingReviewList";
import { RoleForm } from "./RoleForm";
import formStyles from "./MappingForm.module.css";
import styles from "./MappingPanel.module.css";

const EDIT_MODE_OPTIONS: readonly { value: MappingEditMode; label: string }[] = [
  { value: "form", label: "폼" },
  { value: "json", label: "JSON" },
];

/** 적용하지 않은 JSON 편집이 있을 때 저장 · 검증 · POC 를 끈 이유. */
const UNAPPLIED_JSON_TEXT =
  "적용하지 않은 JSON 편집이 있습니다. 매핑 보기의 JSON 방식에서 적용하거나 버려 주세요.";

/** 같은 열을 두 번 고른 칸이 있을 때 검증 · POC 를 끈 이유. 칸의 이름은 바닥 줄이 보인다. */
const REPEATED_COLUMNS_TEXT =
  "같은 열을 두 번 고른 칸이 있습니다. 매핑 보기에서 서로 다른 열을 골라 주세요.";

const LAYOUT_LABEL: Readonly<Record<HandLayout, string>> = {
  single: "한 손(single)",
  left: "왼손(left)",
  right: "오른손(right)",
  both: "양손(left · right)",
};

type MappingPanelProps = {
  view: "mapping" | "validation" | "poc";
  onEditMapping: () => void;
  onMcpDraft: () => void;
  editor: MappingEditor;
  state: MappingEditorState;
  inspect: EpisodeInspect;
  /** 검증 요청의 Probe 와 episode. 칸에 붙이는 issue 는 보고 있는 episode 의 것이다. */
  probeUuid: string;
  sourceId: string;
  /** Probe 샘플의 episode 전부. `샘플 전체 검증` 이 보낸다. */
  sampleSourceIds: readonly string[];
  /** 우측 패널의 스크롤 판 class. 바닥 동작 줄은 스크롤 밖에 선다. */
  scrollClassName: string;
};

/** digest 앞 19자(`sha256:` + 12자)만 보인다. 전체 값은 복사 버튼이 가진다. */
function shortDigest(digest: string): string {
  return digest.length <= 19 ? digest : `${digest.slice(0, 19)}…`;
}

function SavedFacts({ dirty, stored }: { stored: StoredMapping | null; dirty: boolean }) {
  return (
    <section className={styles.saved} aria-label="저장된 매핑">
      {stored === null ? (
        <p className={formStyles.caption}>
          저장된 매핑이 없습니다. 아래에서 매핑을 만들어 저장해 주세요.
        </p>
      ) : (
        <dl className={styles.facts}>
          <dt>저장 상태</dt>
          <dd>{stored.isTemp ? "임시 저장" : "임시 아님"}</dd>
          <dt>digest</dt>
          <dd>
            <span className={styles.digest}>{shortDigest(stored.digest)}</span>
            <CopyButton label="매핑 digest" value={stored.digest} />
          </dd>
          <dt>저장 시각</dt>
          <dd>{formatDateTime(stored.updatedAtMs)}</dd>
        </dl>
      )}
      {dirty ? (
        <p className={styles.dirty} role="status">
          저장하지 않은 변경이 있습니다.
        </p>
      ) : null}
      {stored === null ? null : (
        <details className={styles.json}>
          <summary>저장된 JSON</summary>
          <pre aria-label="매핑 JSON">{formatMappingJson(stored.mapping)}</pre>
        </details>
      )}
    </section>
  );
}

function CameraGroup({
  draft,
  inspect,
  issues,
  onChange,
}: {
  draft: Mapping;
  inspect: EpisodeInspect;
  issues: IssueIndex;
  onChange: (change: (mapping: Mapping) => Mapping) => void;
}) {
  const videoKeys = inspect.videos.map((video) => video.key);
  const rows = cameraRows(draft, videoKeys);
  return (
    <fieldset className={formStyles.group} data-mapping-field="cameras">
      <legend>카메라</legend>
      <IssueList label="카메라 검증 결과" issues={issues.cameras} mapping={draft} />
      <p className={formStyles.caption}>
        관측 카메라는 하나 이상 필요합니다. slot 은 영문 · 숫자 · _ 로 된 서로 다른 이름입니다.
      </p>
      {rows.length === 0 ? (
        <p className={formStyles.caption}>이 episode 에는 카메라가 없습니다.</p>
      ) : null}
      {rows.map(({ index, indexes, source, id, name }) => {
        const camera = draft.cameras[index];
        const missing = source !== null && !videoKeys.includes(source);
        return (
          <div key={id}>
            <div className={styles.camera}>
              <Field
                textVariant={appTextModes["body-normal"]}
                label={missing ? `${name} (이 episode 에 없음)` : name}
                layout="hug"
                labelPlacement="end"
              >
                <Checkbox
                  checked={camera !== undefined}
                  onChange={(event) =>
                    onChange((mapping) =>
                      // 체크 해제는 줄이 맡은 카메라를 지운다. 영상 채널 줄은 원본이 같은 카메라를 모두 맡는다.
                      event.target.checked && source !== null
                        ? addCamera(mapping, source)
                        : removeCameras(mapping, indexes),
                    )
                  }
                />
              </Field>
              {camera === undefined ? null : (
                <Field textVariant={appTextModes["body-normal"]} label="slot" layout="stack">
                  <TextInput
                    aria-label={`${name} slot`}
                    fullWidth
                    inputSize="md"
                    placeholder="slot 예: wrist"
                    value={camera.slot ?? ""}
                    onChange={(event) =>
                      onChange((mapping) => setCameraSlot(mapping, index, event.target.value))
                    }
                  />
                </Field>
              )}
            </div>
            {source === null ? (
              <p className={formStyles.caption}>
                원본을 고르지 않은 카메라입니다. 원본은 JSON 편집 방식에서 적어 주세요.
              </p>
            ) : null}
            <IssueList
              label={`${name} 검증 결과`}
              issues={issues.byCamera.get(index) ?? []}
              mapping={draft}
            />
          </div>
        );
      })}
    </fieldset>
  );
}

function HandEditor({
  editor,
  inspect,
  issues,
  state,
  allFields,
}: {
  editor: MappingEditor;
  inspect: EpisodeInspect;
  issues: IssueIndex;
  state: MappingEditorState;
  allFields: boolean;
}) {
  const { draft, focus } = state;
  const hand = draft.hands[focus.handIndex];
  const vectorChannels = inspect.channels.filter((channel) => channel.kind === "vector");
  const layout = handLayout(draft);
  return (
    <>
      <Field textVariant={appTextModes["body-normal"]} label="손 구성" layout="stack">
        <SingleSelectDropdown
          fullWidth
          label="손 구성"
          options={HAND_LAYOUTS.map((value) => ({ value, label: LAYOUT_LABEL[value] }))}
          placeholder="손 구성을 고르세요"
          size="sm"
          value={layout}
          onValueChange={(value) =>
            editor.edit((mapping) => setHandLayout(mapping, value as HandLayout))
          }
        />
      </Field>
      <IssueList label="손 구성 검증 결과" issues={issues.hands} mapping={draft} />
      {hand === undefined ? null : (
        <>
          {draft.hands.length > 1 ? (
            <ContentSwitcher
              label="편집할 손"
              value={String(focus.handIndex)}
              onValueChange={(value) => editor.setFocus({ ...focus, handIndex: Number(value) })}
              options={draft.hands.map((item, index) => ({
                value: String(index),
                label: HAND_SIDE_LABEL[item.side],
              }))}
            />
          ) : null}
          <ContentSwitcher
            label="편집할 역할"
            value={focus.role}
            onValueChange={(value) => editor.setFocus({ ...focus, role: value as HandRoleName })}
            options={HAND_ROLE_NAMES.map((role) => ({ value: role, label: HAND_ROLE_LABEL[role] }))}
          />
          {focus.role === "action" ? (
            <Field
              textVariant={appTextModes["body-normal"]}
              label="action 매핑"
              layout="hug"
              labelPlacement="end"
            >
              <Switch
                checked={hand.action !== null}
                onChange={(event) =>
                  editor.edit((mapping) =>
                    setActionEnabled(mapping, focus.handIndex, event.target.checked),
                  )
                }
              />
            </Field>
          ) : null}
          {hand[focus.role] === null ? (
            <p className={formStyles.caption}>
              이 손은 action 을 매핑하지 않습니다. action 이 없어도 Direct Teaching 으로 쓸 수
              있습니다.
            </p>
          ) : allFields || ROLE_FIELDS.some((field) => field === state.field) ? (
            <RoleForm
              key={`${focus.handIndex}:${focus.role}`}
              role={hand[focus.role]!}
              field={allFields ? undefined : (state.field as (typeof ROLE_FIELDS)[number])}
              channels={vectorChannels}
              issues={{
                role: issues.byRole.get(roleKey(focus.handIndex, focus.role)) ?? [],
                field: (field) =>
                  issues.byField.get(fieldKey(focus.handIndex, focus.role, field)) ?? [],
                mapping: draft,
              }}
              onChange={(update) =>
                editor.edit((mapping) => updateRole(mapping, focus.handIndex, focus.role, update))
              }
            />
          ) : null}
        </>
      )}
    </>
  );
}

function DeleteMappingModal({
  deleting,
  unreadable = false,
  onCancel,
  onConfirm,
}: {
  deleting: boolean;
  /** 읽지 못하는 저장본을 지우는지. 남길 편집본이 없고 빈 매핑에서 새로 시작한다. */
  unreadable?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open
      closeOnBackdropClick={!deleting}
      closeOnEscape={!deleting}
      size="sm"
      onClose={onCancel}
    >
      <Modal.Header>
        <Modal.Header.Start>
          <Modal.Header.Title>저장된 매핑 삭제</Modal.Header.Title>
        </Modal.Header.Start>
        <Modal.Header.End>
          <Modal.Header.Close disabled={deleting} label="닫기" />
        </Modal.Header.End>
      </Modal.Header>
      <Modal.Body>
        {unreadable ? (
          <p className={formStyles.caption}>
            Revision 에 저장된 매핑을 지웁니다. 지금의 형식으로 읽지 못하는 저장본이라 내용을 되살릴
            수 없습니다. 지운 뒤에는 빈 매핑에서 새로 시작합니다.
          </p>
        ) : (
          <p className={formStyles.caption}>
            Revision 에 저장된 매핑을 지웁니다. 편집 중인 값은 이 화면에 남습니다. Rerun 새 창은
            저장된 매핑을 쓰므로 다시 저장해야 열 수 있습니다. 검증과 POC 는 편집 중인 매핑으로
            실행할 수 있습니다.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className={styles.actions}>
          <IconButton
            disabled={deleting}
            text="취소"
            tone="neutral"
            type="button"
            variant="surface"
            onClick={onCancel}
          />
          <IconButton
            disabled={deleting}
            text={deleting ? "삭제 중" : "삭제"}
            tone="danger"
            type="button"
            variant="solid"
            onClick={onConfirm}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
}

/** 적용하지 않은 JSON 편집을 버리고 폼으로 가기 전에 묻는다. 편집이 없으면 묻지 않는다. */
function DiscardJsonModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal open size="sm" onClose={onCancel}>
      <Modal.Header>
        <Modal.Header.Start>
          <Modal.Header.Title>적용하지 않은 JSON 편집</Modal.Header.Title>
        </Modal.Header.Start>
        <Modal.Header.End>
          <Modal.Header.Close label="닫기" />
        </Modal.Header.End>
      </Modal.Header>
      <Modal.Body>
        <p className={formStyles.caption}>
          JSON 방식에 적용하지 않은 편집이 있습니다. 폼으로 가면 이 편집을 버립니다. 편집을 남기려면
          취소한 뒤 적용해 주세요.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <div className={styles.actions}>
          <IconButton
            text="취소"
            tone="neutral"
            type="button"
            variant="surface"
            onClick={onCancel}
          />
          <IconButton
            text="편집 버리기"
            tone="danger"
            type="button"
            variant="solid"
            onClick={onConfirm}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
}

/** 편집을 버리고 서버 저장본을 다시 받기 전에 묻는다. 편집이 없으면 묻지 않는다. */
function ReloadMappingModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal open size="sm" onClose={onCancel}>
      <Modal.Header>
        <Modal.Header.Start>
          <Modal.Header.Title>서버에서 다시 불러오기</Modal.Header.Title>
        </Modal.Header.Start>
        <Modal.Header.End>
          <Modal.Header.Close label="닫기" />
        </Modal.Header.End>
      </Modal.Header>
      <Modal.Body>
        <p className={formStyles.caption}>
          저장하지 않은 편집과 적용하지 않은 JSON 편집을 버리고 Revision 에 저장된 매핑을 다시
          불러옵니다. 저장된 매핑이 없으면 빈 매핑에서 시작합니다.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <div className={styles.actions}>
          <IconButton
            text="취소"
            tone="neutral"
            type="button"
            variant="surface"
            onClick={onCancel}
          />
          <IconButton
            text="편집 버리고 불러오기"
            tone="danger"
            type="button"
            variant="solid"
            onClick={onConfirm}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * 칸에 붙일 issue — 보고 있는 episode 의 결과이고, 결과가 지금 편집본의 것일 때만이다. 편집본이 바뀌었으면
 * 번호가 다른 칸을 가리킬 수 있다.
 */
function attachedIssues(state: MappingEditorState, sourceId: string): IssueIndex {
  const { validation } = state;
  if (validation.status !== "done" || !sameMapping(validation.sent, state.draft)) {
    return EMPTY_ISSUE_INDEX;
  }
  const current = validation.result.episodes.find((episode) => episode.sourceId === sourceId);
  return current === undefined ? EMPTY_ISSUE_INDEX : indexIssues(current.issues, validation.sent);
}

/**
 * 검증 묶음 — 버튼 둘과 episode 별 결과, 칸에 붙지 않는 issue.
 * 결과가 지금 편집본의 것이 아니면 칸에 붙이지 않고 여기 목록으로만 보인다.
 */
function ValidationGroup({
  editor,
  probeUuid,
  sampleSourceIds,
  sourceId,
  state,
  onEditIssue,
}: {
  editor: MappingEditor;
  probeUuid: string;
  sourceId: string;
  sampleSourceIds: readonly string[];
  state: MappingEditorState;
  onEditIssue: (issue: MappingIssue) => void;
}) {
  const { validation } = state;
  const running = validation.status === "running";
  const disabled = running || !canSendDraft(state);
  const done = validation.status === "done" ? validation : null;
  const stale =
    done !== null && (!sameMapping(done.sent, state.draft) || done.result.probeUuid !== probeUuid);
  const current = done?.result.episodes.find((episode) => episode.sourceId === sourceId) ?? null;
  return (
    <fieldset className={formStyles.group}>
      <legend>검증</legend>
      <p className={formStyles.caption}>
        편집본의 TCP 근거와 실제 샘플의 수치·시간을 검사합니다. 서버 검증 기록을 남깁니다. 매핑
        저장은 별도입니다. 영상 디코드는 POC가 검사합니다.
      </p>

      <div className={styles.inlineActions}>
        <IconButton
          disabled={disabled}
          text="이 episode 검증"
          type="button"
          variant="surface"
          onClick={() =>
            void editor.validate({ probeUuid, sourceIds: [sourceId], scope: "episode" })
          }
        />
        <IconButton
          disabled={disabled || sampleSourceIds.length === 0}
          text={`샘플 전체 검증(${sampleSourceIds.length})`}
          type="button"
          variant="surface"
          onClick={() =>
            void editor.validate({ probeUuid, sourceIds: sampleSourceIds, scope: "sample" })
          }
        />
      </div>
      {running ? <Spinner label="검증하는 중" size="sm" /> : null}
      {validation.status === "failed" ? (
        <p className={formStyles.error} role="alert">
          {validation.message}
        </p>
      ) : null}
      {done === null ? null : (
        <>
          {stale ? (
            <p className={styles.dirty} role="status">
              이 결과는 검증할 때의 매핑 결과입니다. 그 뒤에 편집했으므로 칸에 붙이지 않습니다. 다시
              검증해 주세요.
            </p>
          ) : null}
          <ul className={styles.episodes} aria-label="episode 별 검증 결과">
            {done.result.episodes.map((episode) => {
              const errors = episode.issues.filter((issue) => issue.severity === "error").length;
              const warnings = episode.issues.length - errors;
              return (
                <li key={episode.sourceId}>
                  <div>{episode.sourceId}</div>
                  <div>
                    {episode.sourceId === sourceId ? "보고 있는 episode · " : ""}
                    {episode.valid ? "통과" : `오류 ${errors}`}
                    {warnings > 0 ? ` · 경고 ${warnings}` : ""}
                  </div>
                </li>
              );
            })}
          </ul>
          <ul className={styles.episodes} aria-label="TCP 근거 검사 결과">
            {done.result.tcpAssessments.map((assessment) => (
              <li key={assessment.path}>
                <span>
                  {assessment.path} ·{" "}
                  {assessment.status === "blocked"
                    ? "근거 보완 필요"
                    : assessment.status === "requires_experimental_review"
                      ? "추론 후보 · 실험 검토 필요"
                      : "근거 구조 검사 통과 · 표본 검증 필요"}
                </span>
                <IssueList
                  label="TCP 근거 문제"
                  issues={assessment.issues}
                  mapping={done.sent}
                  onEditIssue={stale ? undefined : onEditIssue}
                />
              </li>
            ))}
          </ul>
          <p className={formStyles.caption}>검증 통과는 실제 TCP 확인을 뜻하지 않습니다.</p>
          {current === null ? (
            <p className={formStyles.caption}>보고 있는 episode 는 이번에 검증하지 않았습니다.</p>
          ) : (
            <IssueList
              label="매핑 전체 검증 결과"
              issues={current.issues}
              mapping={done.sent}
              onEditIssue={stale ? undefined : onEditIssue}
            />
          )}
        </>
      )}
    </fieldset>
  );
}

/**
 * 일반 저장을 막은 자리. issue 경로는 그때 보낸 매핑의 것이라 그 매핑으로 이름을 푼다. 편집본이 바뀌었으면
 * 번호가 다른 칸을 가리킬 수 있어 `수정` 을 두지 않는다(검증 결과와 같은 규칙).
 */
function SaveIssueList({
  saveIssues,
  draft,
  onEditIssue,
}: {
  saveIssues: SaveIssues;
  draft: Mapping;
  onEditIssue: (issue: MappingIssue) => void;
}) {
  const stale = !sameMapping(saveIssues.sent, draft);
  return (
    <Scrollbar className={styles.saveIssues} gutter="auto">
      {stale ? (
        <p className={formStyles.caption}>
          저장을 시도한 뒤 편집본이 바뀌었습니다. 아래는 그때의 결과입니다. 다시 저장하면 새로
          확인합니다.
        </p>
      ) : null}
      <IssueList
        label="저장을 막은 항목"
        issues={saveIssues.issues}
        mapping={saveIssues.sent}
        onEditIssue={stale ? undefined : onEditIssue}
      />
    </Scrollbar>
  );
}

const POC_STATE_VIEW: Readonly<Record<PocJobState, { tone: StatusTone; label: string }>> = {
  queued: { tone: "job.pending", label: "대기" },
  running: { tone: "job.running", label: "실행 중" },
  succeeded: { tone: "job.succeeded", label: "성공" },
  failed: { tone: "job.failed", label: "실패" },
};

/** POC 기술 정보 — 작업 · 입력 식별자와 워커 보고서 원문. */
function pocDetails(job: PocJob): string {
  return [
    `job: ${job.uuid}`,
    `mapping_digest: ${job.mappingDigest}`,
    `input_digest: ${job.inputDigest}`,
    `sample: ${job.sampleUuid}`,
    `engine: ${job.engineVersion} · templates: ${job.templatesVersion} · ${job.implementation}`,
    `output_artifact: ${job.outputArtifactUuid ?? "—"}`,
    ...(job.error === null ? [] : [`error: ${job.error}`]),
    ...(job.result === null ? [] : [`result: ${JSON.stringify(job.result, null, 2)}`]),
  ].join("\n");
}

/**
 * POC 묶음 — 편집본으로 실제 변환 · 영상 · deep validation 을 접수하고 끝까지 따라간다.
 * 결과는 접수할 때의 매핑에 속한다. 그 뒤에 편집했으면 그 사실을 말한다.
 */
function PocGroup({
  editor,
  probeUuid,
  state,
}: {
  editor: MappingEditor;
  probeUuid: string;
  state: MappingEditorState;
}) {
  const { poc } = state;
  const [reviewNote, setReviewNote] = useState("");
  const validation = currentPocValidation(state, probeUuid);
  const needsReview = validation !== null && pocNeedsReview(validation);
  const reviewed =
    state.review.status === "done" &&
    state.review.result.validationUuid === validation?.validationUuid;
  const tracking = poc.status === "tracking" ? poc : null;
  const busy =
    poc.status === "requesting" || (tracking !== null && !isPocFinished(tracking.job.state));
  const stale =
    tracking !== null &&
    (!sameMapping(tracking.sent, state.draft) || tracking.job.probeUuid !== probeUuid);
  return (
    <fieldset className={formStyles.group}>
      <legend>POC</legend>
      <p className={formStyles.caption}>
        편집본으로 실제 원본의 앞 3개 episode 를 변환하고 영상 디코드 · deep validation 을 합니다.
        모든 episode 가 통과해야 성공입니다. 기록은 이 화면에만 남습니다. 새로고침하면 진행 상태를
        다시 볼 수 없습니다.
      </p>
      <p className={formStyles.caption}>
        실행 대상: 현재 편집본 · Probe {probeUuid} · 원본 앞 3개 episode
      </p>
      <p className={styles.dirty}>
        샘플 검증: {validationSummary(state, probeUuid)}. TCP 근거와 샘플 전체 검증을 통과해야
        접수할 수 있습니다. 추론 후보는 해당 후보의 명시적 검토 기록도 필요합니다.
      </p>
      {needsReview ? (
        <div className={formStyles.form}>
          <p className={formStyles.caption}>
            매핑·표본·계산 버전에 묶인 실험 검토입니다. 후보의 자료, 다른 후보, 관측 구간과 남은
            불확실성을 확인하세요.
          </p>
          <Field textVariant={appTextModes["body-normal"]} label="후보 검토 메모" layout="stack">
            <TextInput
              fullWidth
              value={reviewNote}
              maxLength={2000}
              placeholder="검토한 후보와 남은 불확실성"
              onChange={(event) => setReviewNote(event.target.value)}
            />
          </Field>
          <IconButton
            text="후보를 검토했으며 실험 POC 검토 기록을 남김"
            type="button"
            variant="surface"
            disabled={reviewed || state.review.status === "running" || reviewNote.trim() === ""}
            onClick={() => void editor.reviewCandidate(probeUuid, reviewNote)}
          />
          {reviewed ? <p>현재 후보의 검토 기록이 있습니다.</p> : null}
        </div>
      ) : null}
      {state.review.status === "failed" ? (
        <p className={formStyles.error} role="alert">
          {state.review.message}
        </p>
      ) : null}
      <div className={styles.inlineActions}>
        <IconButton
          disabled={busy || !canRequestPoc(state, probeUuid)}
          text={poc.status === "requesting" ? "접수 중" : "POC 요청"}
          type="button"
          variant="surface"
          onClick={() => void editor.requestPoc(probeUuid)}
        />
      </div>
      {poc.status === "failed" ? (
        <div>
          <p className={formStyles.error} role="alert">
            {poc.message}
          </p>
          {poc.detail === null ? null : (
            <details className={styles.json}>
              <summary>기술 정보</summary>
              <pre>{poc.detail}</pre>
            </details>
          )}
        </div>
      ) : null}
      {tracking === null ? null : (
        <section className={styles.poc} aria-label="POC 상태">
          <div className={styles.pocHead}>
            <StatusChip tone={POC_STATE_VIEW[tracking.job.state].tone}>
              {POC_STATE_VIEW[tracking.job.state].label}
            </StatusChip>
            <span>
              시도 {tracking.job.attempt} · episode {tracking.job.sourceIds.length}개 · 갱신{" "}
              {formatDateTime(tracking.job.updatedAtMs)}
            </span>
          </div>
          <details className={styles.json}>
            <summary>episode 목록</summary>
            <ul className={styles.episodes}>
              {tracking.job.sourceIds.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          </details>
          {tracking.job.state === "succeeded" ? (
            <p className={formStyles.caption}>
              {tracking.job.experimental ? "실험 POC" : "POC"}가 성공했습니다. 고정한 episode가 모두
              통과했습니다. 실제 TCP 확인을 뜻하지 않습니다.
            </p>
          ) : null}
          {tracking.job.state === "failed" ? (
            <p className={formStyles.error}>
              POC 가 실패했습니다. 기술 정보의 오류를 확인해 주세요.
            </p>
          ) : null}
          {stale ? (
            <p className={styles.dirty} role="status">
              이 POC 는 접수할 때의 매핑 결과입니다. 지금 편집본과 다릅니다.
            </p>
          ) : null}
          {tracking.pollError === null ? null : (
            <p className={formStyles.caption}>{tracking.pollError}</p>
          )}
          <details className={styles.json}>
            <summary>기술 정보</summary>
            <pre>{pocDetails(tracking.job)}</pre>
          </details>
        </section>
      )}
    </fieldset>
  );
}

function announceSave(outcome: SaveOutcome, isTemp: boolean): void {
  if (outcome.created) {
    toast.success(isTemp ? "매핑을 임시 저장했습니다." : "매핑을 저장했습니다.");
  } else {
    toast.success(isTemp ? "저장된 매핑을 임시 저장으로 바꿨습니다." : "저장된 매핑을 바꿨습니다.");
  }
}

/**
 * 우측 패널의 `매핑` 보기 — Revision 매핑을 편집하고 저장한다.
 *
 * 편집 방식은 `폼` 과 `JSON` 둘이다. 폼은 손 하나 × 역할 하나를 보인다. 칸을 바꾸면 편집본만 바뀐다. JSON 은
 * 편집본 전체를 wire JSON 으로 보이고, 적용할 때만 편집본을 바꾼다. 적용하지 않은 JSON 편집이 있으면 폼으로
 * 가기 전에 묻고 저장 · 검증 · POC 를 끈다. 저장은 `임시 저장` · `저장` 버튼이 한다. 저장은 수치 검증을
 * 하지 않는다.
 * 방식 전환과 동작 줄은 스크롤 밖에 선다.
 */
export function MappingPanel({
  editor,
  inspect,
  probeUuid,
  sampleSourceIds,
  scrollClassName,
  sourceId,
  state,
  view,
  onEditMapping,
  onMcpDraft,
}: MappingPanelProps) {
  const [allFields, setAllFields] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingReload, setConfirmingReload] = useState(false);
  // 적용하지 않은 JSON 편집을 버린 뒤에 할 일. 값이 있으면 확인 Modal 이 선다.
  const [afterDiscard, setAfterDiscard] = useState<(() => void) | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const [pendingField, setPendingField] = useState<string | null>(null);
  // 필수 칸 판정과 이름 붙이기는 편집본이 바뀔 때만 다시 한다.
  const reviewItems = useMemo(
    () => mappingReviewItems({ draft: state.draft }, inspect),
    [state.draft, inspect],
  );
  const { saved } = state;
  useEffect(() => {
    if (view !== "mapping" || state.mode !== "form" || pendingField === null) return;
    const target = formRef.current?.querySelector<HTMLElement>(
      `[data-mapping-field~="${pendingField}"]`,
    );
    target?.scrollIntoView({ block: "nearest" });
    target?.querySelector<HTMLElement>("input, button, textarea")?.focus({ preventScroll: true });
    setPendingField(null);
  }, [view, state.mode, pendingField]);

  /** 적용하지 않은 JSON 편집이 있으면 버릴지 먼저 묻고, 없으면 곧바로 한다. */
  const leaveJson = (next: () => void): void => {
    if (hasUnappliedJson(state)) {
      setAfterDiscard(() => next);
    } else {
      next();
    }
  };

  const changeMode = (mode: MappingEditMode): void => {
    if (mode === "form") {
      leaveJson(() => editor.setMode("form"));
    } else {
      editor.setMode(mode);
    }
  };

  /**
   * 고칠 자리로 간다. 확인 필요 항목과 issue 목록의 `수정` 이 같이 쓴다. 자리는 `issueTarget` 이 정한다. 서버
   * 경로를 화면에서 다시 파싱하지 않는다. 폼에 없는 칸은 JSON 방식에서 적는다.
   */
  const goTo = (target: IssueTarget): void => {
    if (target.kind === "json") {
      editor.setMode("json");
      onEditMapping();
      return;
    }
    leaveJson(() => {
      if (target.focus) editor.setFocus(target.focus);
      editor.setMode("form");
      editor.setField(target.field);
      setAllFields(false);
      setPendingField(target.field);
      onEditMapping();
    });
  };

  const editIssue = (issue: MappingIssue): void => {
    const target = issueTarget(issue.path, state.draft);
    if (target !== null) goTo(target);
  };

  if (saved.status === "loading") {
    return <Spinner label="저장된 매핑을 불러오는 중" size="sm" />;
  }
  if (saved.status === "no_revision") {
    return (
      <p className={formStyles.caption}>
        이 Probe 에는 Revision 이 없습니다. 그래서 매핑을 읽거나 저장할 수 없습니다.
      </p>
    );
  }
  if (saved.status === "failed") {
    const discard = async (): Promise<void> => {
      if (await editor.discardUnreadable()) {
        toast.success("저장된 매핑을 지웠습니다. 빈 매핑에서 새로 시작합니다.");
      }
      setConfirmingDelete(false);
    };
    return (
      <div className={styles.failed}>
        <p className={formStyles.error} role="alert">
          {saved.message}
        </p>
        {state.commandError === null ? null : (
          <p className={formStyles.error} role="alert">
            {state.commandError}
          </p>
        )}
        <IconButton
          disabled={state.pending !== null}
          text="다시 시도"
          variant="surface"
          onClick={() => void editor.load()}
        >
          <RotateCwIcon />
        </IconButton>
        {saved.unreadable ? (
          <IconButton
            disabled={state.pending !== null}
            text="저장 삭제"
            tone="danger"
            variant="surface"
            onClick={() => setConfirmingDelete(true)}
          />
        ) : null}
        {confirmingDelete ? (
          <DeleteMappingModal
            deleting={state.pending === "delete"}
            unreadable
            onCancel={() => setConfirmingDelete(false)}
            onConfirm={() => void discard()}
          />
        ) : null}
      </div>
    );
  }

  const busy = state.pending !== null;
  const unapplied = hasUnappliedJson(state);
  const issues = attachedIssues(state, sourceId);
  const repeated = repeatedColumnPaths(state.draft);
  const save = async (isTemp: boolean): Promise<void> => {
    const outcome = await editor.save(isTemp);
    if (outcome !== null) {
      announceSave(outcome, isTemp);
    }
  };
  const remove = async (): Promise<void> => {
    if (await editor.remove()) {
      toast.success("저장된 매핑을 지웠습니다.");
    }
    setConfirmingDelete(false);
  };
  const reload = (): void => {
    setConfirmingReload(false);
    void editor.load();
  };

  const jsonMode = view === "mapping" && state.mode === "json";
  return (
    <>
      <MappingRefreshStatus state={state} />
      {view === "mapping" ? (
        <details className={styles.details} open={state.mode === "json" || undefined}>
          <summary>전체 필드·JSON·저장 정보</summary>
          <Scrollbar className={styles.detailsScroll} gutter="auto">
            <div className={formStyles.form}>
              <ContentSwitcher
                label="편집 방식"
                value={state.mode}
                onValueChange={(value) => changeMode(value as MappingEditMode)}
                options={EDIT_MODE_OPTIONS}
              />
              <Field
                textVariant={appTextModes["body-normal"]}
                label="전체 필드 펼치기"
                layout="hug"
                labelPlacement="end"
              >
                <Switch
                  checked={allFields}
                  onChange={(event) => setAllFields(event.target.checked)}
                />
              </Field>
              <SavedFacts stored={saved.status === "saved" ? saved.stored : null} dirty={false} />
            </div>
          </Scrollbar>
        </details>
      ) : null}
      {jsonMode ? (
        <MappingJsonPane editor={editor} state={state} />
      ) : (
        <Scrollbar
          key={view}
          className={scrollClassName}
          gutter="auto"
          aria-label={view === "mapping" ? "매핑 편집" : "검증·POC 내용"}
        >
          <div className={formStyles.form} ref={formRef}>
            {view === "validation" || view === "poc" ? (
              <>
                {unapplied ? (
                  <p className={styles.dirty} role="status">
                    {UNAPPLIED_JSON_TEXT}
                  </p>
                ) : null}
                {repeated.length > 0 ? (
                  <p className={styles.dirty} role="status">
                    {REPEATED_COLUMNS_TEXT}
                  </p>
                ) : null}
                {view === "validation" ? (
                  <ValidationGroup
                    editor={editor}
                    probeUuid={probeUuid}
                    sampleSourceIds={sampleSourceIds}
                    sourceId={sourceId}
                    state={state}
                    onEditIssue={editIssue}
                  />
                ) : (
                  <PocGroup editor={editor} probeUuid={probeUuid} state={state} />
                )}
              </>
            ) : (
              <>
                {saved.status === "none" &&
                state.draft.hands.length === 0 &&
                state.field !== "hands" &&
                !allFields ? (
                  <section className={styles.review} aria-label="매핑 시작">
                    <strong>매핑을 준비하세요</strong>
                    <p>매핑을 불러오거나 직접 작성한 뒤 영상과 핸드의 방향을 비교하세요.</p>
                    <IconButton text="MCP 연결 확인" variant="solid" onClick={onMcpDraft} />
                    <IconButton
                      text="직접 작성"
                      variant="surface"
                      onClick={() => editor.setField("hands")}
                    />
                  </section>
                ) : (
                  <>
                    <MappingReviewList
                      items={reviewItems}
                      onSelect={({ target }) => goTo(target)}
                    />
                    <IssueList
                      label="확인할 검증 항목"
                      issues={
                        state.validation.status === "done" &&
                        state.validation.result.probeUuid === probeUuid &&
                        sameMapping(state.validation.sent, state.draft)
                          ? (state.validation.result.episodes.find(
                              (episode) => episode.sourceId === sourceId,
                            )?.issues ?? [])
                          : []
                      }
                      mapping={state.draft}
                      onEditIssue={editIssue}
                    />
                    <Field
                      textVariant={appTextModes["body-normal"]}
                      label="선택 필드"
                      layout="stack"
                    >
                      <SingleSelectDropdown
                        fullWidth
                        label="선택 필드"
                        size="sm"
                        value={state.field}
                        options={[
                          { value: "hands", label: "손 구성" },
                          ...ROLE_FIELDS.map((field) => ({
                            value: field,
                            label: ROLE_FIELD_LABEL[field],
                          })),
                          { value: "cameras", label: "카메라" },
                          { value: "time", label: "공통 시간 이동" },
                        ]}
                        onValueChange={(value) => editor.setField(value as MappingField)}
                      />
                    </Field>
                    {allFields || (state.field !== "cameras" && state.field !== "time") ? (
                      <div className={formStyles.form} data-mapping-field="hands">
                        <HandEditor
                          editor={editor}
                          inspect={inspect}
                          issues={issues}
                          state={state}
                          allFields={allFields}
                        />
                      </div>
                    ) : null}
                    {allFields || state.field === "cameras" ? (
                      <CameraGroup
                        draft={state.draft}
                        inspect={inspect}
                        issues={issues}
                        onChange={(change) => editor.edit(change)}
                      />
                    ) : null}
                    {allFields || state.field === "time" ? (
                      <fieldset className={formStyles.group} data-mapping-field="time">
                        <legend>공통 시간 이동</legend>
                        <Field
                          textVariant={appTextModes["body-normal"]}
                          label="이동(초)"
                          layout="stack"
                        >
                          <NumberInput
                            fullWidth
                            inputSize="md"
                            mode="decimal"
                            value={state.draft.time_shift_seconds}
                            onChange={(value) => {
                              if (value !== null) {
                                editor.edit((mapping) => ({
                                  ...mapping,
                                  time_shift_seconds: value,
                                }));
                              }
                            }}
                          />
                        </Field>
                        <p className={formStyles.caption}>
                          모든 채널에 같은 값을 더합니다. 채널 사이의 시간차는 그대로입니다.
                        </p>
                      </fieldset>
                    ) : null}
                  </>
                )}
              </>
            )}
          </div>
        </Scrollbar>
      )}
      <div className={styles.footer}>
        {state.dirty ? (
          <p className={styles.dirty} role="status">
            저장하지 않은 변경이 있습니다.
          </p>
        ) : null}
        {repeated.length > 0 ? (
          <p className={formStyles.caption} role="status">
            같은 열을 두 번 고른 칸이 있어 저장 · 검증 · POC 를 할 수 없습니다:{" "}
            {repeated.map((path) => describeMappingPath(state.draft, path)).join(" · ")}. 서로 다른
            열을 골라 주세요.
          </p>
        ) : null}
        {unapplied ? (
          <p className={formStyles.caption} role="status">
            적용하지 않은 JSON 편집이 있어 저장할 수 없습니다. 적용하거나 버려 주세요.
          </p>
        ) : null}
        {state.commandError === null ? null : (
          <p className={formStyles.error} role="alert">
            {state.commandError}
          </p>
        )}
        {state.saveIssues === null ? null : (
          <SaveIssueList
            saveIssues={state.saveIssues}
            draft={state.draft}
            onEditIssue={editIssue}
          />
        )}
        <div className={styles.actions}>
          <Menu label="매핑 작업">
            <Menu.Trigger label="매핑 작업" size="sm" />
            <Menu.Item disabled={!hasUnsavedEdits(state) || busy} onSelect={() => editor.revert()}>
              되돌리기
            </Menu.Item>
            <Menu.Item
              disabled={!isEditable(state) || busy}
              onSelect={() => {
                if (hasUnsavedEdits(state)) {
                  setConfirmingReload(true);
                } else {
                  reload();
                }
              }}
            >
              서버에서 다시 불러오기
            </Menu.Item>
            <Menu.Item
              disabled={saved.status !== "saved" || busy}
              onSelect={() => setConfirmingDelete(true)}
            >
              저장 삭제
            </Menu.Item>
          </Menu>
          <IconButton
            disabled={busy || !canSendDraft(state)}
            text="임시 저장"
            tone="neutral"
            type="button"
            variant="surface"
            onClick={() => void save(true)}
          />
          <IconButton
            disabled={busy || !canSendDraft(state)}
            text={state.pending === "save" ? "저장 중" : "저장"}
            tone="primary"
            type="button"
            variant="solid"
            onClick={() => void save(false)}
          />
        </div>
      </div>
      {afterDiscard === null ? null : (
        <DiscardJsonModal
          onCancel={() => setAfterDiscard(null)}
          onConfirm={() => {
            setAfterDiscard(null);
            editor.discardJson();
            afterDiscard();
          }}
        />
      )}
      {confirmingReload ? (
        <ReloadMappingModal onCancel={() => setConfirmingReload(false)} onConfirm={reload} />
      ) : null}
      {confirmingDelete ? (
        <DeleteMappingModal
          deleting={state.pending === "delete"}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => void remove()}
        />
      ) : null}
    </>
  );
}
